const PITCH: Record<string, number> = {
  C: 0,
  "B#": 0,
  "C#": 1,
  Db: 1,
  D: 2,
  "D#": 3,
  Eb: 3,
  E: 4,
  Fb: 4,
  F: 5,
  "E#": 5,
  "F#": 6,
  Gb: 6,
  G: 7,
  "G#": 8,
  Ab: 8,
  A: 9,
  "A#": 10,
  Bb: 10,
  B: 11,
  Cb: 11,
};

const SHARPS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLATS = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
const MAJOR_TRAD = ["C", "Db", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];
const MINOR_TRAD = ["Cm", "C#m", "Dm", "Ebm", "Em", "Fm", "F#m", "Gm", "G#m", "Am", "Bbm", "Bm"];
const FLAT_ROOTS = new Set(["Bb", "Eb", "Ab", "Db", "Gb", "Cb", "F"]);
const FLAT_MINOR_ROOTS = new Set(["D", "G", "C", "F", "Bb", "Eb", "Ab"]);
const QUALITY_TOKENS = ["maj", "min", "dim", "aug", "sus", "add", "m", "M", "+", "°", "ø", "-", "#", "b"];

export const MAJOR_KEY_OPTIONS = ["C", "C#", "Db", "D", "D#", "Eb", "E", "F", "F#", "Gb", "G", "G#", "Ab", "A", "A#", "Bb", "B"];
export const MINOR_KEY_OPTIONS = MAJOR_KEY_OPTIONS.map((key) => `${key}m`);
export const KEY_OPTIONS = [...MAJOR_KEY_OPTIONS, ...MINOR_KEY_OPTIONS];

export interface ChordSymbol {
  root: string;
  quality: string;
  bass: string | null;
}

export interface ChartPart {
  chord: string | null;
  lyric: string;
}

export type ChartBlock =
  | { type: "section"; label: string }
  | { type: "lyrics"; parts: ChartPart[] }
  | { type: "chords"; chords: string[] }
  | { type: "text"; text: string }
  | { type: "spacer" };

export interface TransposeResult {
  chart: string;
  key: string;
  concertKey: string;
  semitones: number;
  capo: number;
}

function cleanAccidental(value: string): string {
  return value.replaceAll("♯", "#").replaceAll("♭", "b");
}

export function normalizeKey(key: string): string {
  const match = cleanAccidental(key.trim()).match(/^([A-G](?:#|b)?)(?:\s*)(m|min|minor|maj|major)?$/i);
  if (!match) throw new Error(`Unknown key: ${key}`);
  const root = match[1] ?? "";
  const letter = root[0]?.toUpperCase() ?? "";
  const accidental = root.slice(1);
  const canonicalRoot = `${letter}${accidental}`;
  if (PITCH[canonicalRoot] === undefined) throw new Error(`Unknown key: ${key}`);
  const quality = (match[2] ?? "").toLowerCase();
  const minor = quality === "m" || quality === "min" || quality === "minor";
  return `${canonicalRoot}${minor ? "m" : ""}`;
}

export function prefersFlats(key: string): boolean {
  const canonical = normalizeKey(key);
  const minor = canonical.endsWith("m");
  const root = minor ? canonical.slice(0, -1) : canonical;
  if (FLAT_ROOTS.has(root)) return true;
  return minor && FLAT_MINOR_ROOTS.has(root);
}

function pitchOfKey(key: string): number {
  const canonical = normalizeKey(key);
  const root = canonical.endsWith("m") ? canonical.slice(0, -1) : canonical;
  const pitch = PITCH[root];
  if (pitch === undefined) throw new Error(`Unknown key: ${key}`);
  return pitch;
}

function spell(pitch: number, flats: boolean): string {
  const index = ((pitch % 12) + 12) % 12;
  return (flats ? FLATS : SHARPS)[index] ?? "C";
}

export function transposeKeyName(key: string, semitones: number): string {
  const canonical = normalizeKey(key);
  const minor = canonical.endsWith("m");
  const pitch = (pitchOfKey(canonical) + semitones) % 12;
  const index = (pitch + 12) % 12;
  return (minor ? MINOR_TRAD : MAJOR_TRAD)[index] ?? canonical;
}

function isNote(value: string): boolean {
  return /^[A-G](?:#|b)?$/i.test(cleanAccidental(value));
}

export function parseChordSymbol(symbol: string): ChordSymbol | null {
  const cleaned = cleanAccidental(symbol.trim());
  const match = cleaned.match(/^([A-G])([#b])?(.*)$/i);
  if (!match) return null;
  const root = `${(match[1] ?? "").toUpperCase()}${match[2] ?? ""}`;
  if (PITCH[root] === undefined) return null;
  const rest = match[3] ?? "";
  let index = 0;
  let quality = "";
  let bass: string | null = null;
  while (index < rest.length) {
    const char = rest[index] ?? "";
    if (char === "/") {
      const after = rest.slice(index + 1);
      if (/^\d+$/.test(after)) {
        quality += `/${after}`;
        index = rest.length;
        break;
      }
      const bassMatch = after.match(/^([A-G])([#b])?$/i);
      if (!bassMatch) return null;
      bass = `${(bassMatch[1] ?? "").toUpperCase()}${bassMatch[2] ?? ""}`;
      if (PITCH[bass] === undefined) return null;
      index = rest.length;
      break;
    }
    if (/\d/.test(char) || char === "(" || char === ")") {
      quality += char;
      index += 1;
      continue;
    }
    const slice = rest.slice(index);
    const token = QUALITY_TOKENS.find((item) => slice.startsWith(item));
    if (!token) return null;
    quality += token;
    index += token.length;
  }
  if (!isNote(root)) return null;
  return { root, quality, bass };
}

export function formatChord(symbol: ChordSymbol, flats: boolean, semitones: number): string {
  const root = spell((PITCH[symbol.root] ?? 0) + semitones, flats);
  const bass = symbol.bass ? `/${spell((PITCH[symbol.bass] ?? 0) + semitones, flats)}` : "";
  return `${root}${symbol.quality}${bass}`;
}

export function transposeSymbol(symbol: string, semitones: number, flats: boolean): string {
  const parsed = parseChordSymbol(symbol);
  if (!parsed) return symbol;
  return formatChord(parsed, flats, semitones);
}

function isChordLine(line: string): boolean {
  if (line.includes("[") || line.includes("{") || line.trim().startsWith("#")) return false;
  const tokens = line.trim().split(/\s+/).filter(Boolean);
  return tokens.length > 0 && tokens.every((token) => parseChordSymbol(token) !== null);
}

function transposeLine(line: string, semitones: number, flats: boolean, targetKey: string): string {
  const directive = line.match(/^\{(\s*key\s*):\s*([^}]+)\}$/i);
  if (directive) return `{${directive[1]}: ${targetKey}}`;
  if (line.trim().startsWith("#") || line.trim().startsWith("##")) return line;
  if (line.includes("[")) {
    return line.replaceAll(/\[([^\]]+)\]/g, (full, inner: string) => {
      const parsed = parseChordSymbol(inner);
      return parsed ? `[${formatChord(parsed, flats, semitones)}]` : full;
    });
  }
  if (!isChordLine(line)) return line;
  return line.replaceAll(/\S+/g, (token) => transposeSymbol(token, semitones, flats));
}

export function transposeChart(chart: string, fromKey: string, concertKey: string, capo = 0): TransposeResult {
  const from = normalizeKey(fromKey);
  const concert = normalizeKey(concertKey);
  const safeCapo = Number.isFinite(capo) ? Math.max(0, Math.min(11, Math.trunc(capo))) : 0;
  const concertSemis = (pitchOfKey(concert) - pitchOfKey(from) + 12) % 12;
  const displaySemis = concertSemis - safeCapo;
  const displayKey = safeCapo === 0 ? concert : transposeKeyName(concert, -safeCapo);
  const flats = prefersFlats(displayKey);
  const next = chart
    .replaceAll("\r\n", "\n")
    .split("\n")
    .map((line) => transposeLine(line, displaySemis, flats, displayKey))
    .join("\n");
  return { chart: next, key: displayKey, concertKey: concert, semitones: displaySemis, capo: safeCapo };
}

export function parseLyricLine(line: string): ChartPart[] {
  const parts: ChartPart[] = [];
  const expression = /\[([^\]]+)\]/g;
  let index = 0;
  let chord: string | null = null;
  for (const match of line.matchAll(expression)) {
    const start = match.index ?? 0;
    parts.push({ chord, lyric: line.slice(index, start) });
    chord = match[1] ?? null;
    index = start + match[0].length;
  }
  parts.push({ chord, lyric: line.slice(index) });
  if (parts.length > 1 && parts[0]?.chord === null && parts[0]?.lyric === "") parts.shift();
  return parts.length > 0 ? parts : [{ chord: null, lyric: line }];
}

export function parseChart(chart: string): ChartBlock[] {
  return chart.replaceAll("\r\n", "\n").split("\n").map((line) => {
    const trimmed = line.trim();
    if (!trimmed) return { type: "spacer" } as const;
    if (trimmed.startsWith("##")) return { type: "section", label: trimmed.replace(/^#+\s*/, "") } as const;
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      const comment = trimmed.match(/^\{(?:comment|c):\s*([^}]+)\}$/i);
      if (comment) return { type: "text", text: comment[1]?.trim() ?? "" } as const;
      return { type: "spacer" } as const;
    }
    if (trimmed.startsWith("#")) return { type: "text", text: trimmed.replace(/^#\s*/, "") } as const;
    if (isChordLine(line)) return { type: "chords", chords: trimmed.split(/\s+/) } as const;
    return { type: "lyrics", parts: parseLyricLine(line) } as const;
  });
}

export function chartPlainLines(chart: string): string[] {
  return parseChart(chart).flatMap((block) => {
    if (block.type === "section") return [block.label.toUpperCase()];
    if (block.type === "text") return [block.text];
    if (block.type === "chords") return [block.chords.join("  ")];
    if (block.type === "lyrics") return [block.parts.map((part) => `${part.chord ? `(${part.chord}) ` : ""}${part.lyric}`).join("")];
    return [""];
  });
}
